/**
 * Utility functions for calculating reading time
 */

/**
 * Calculates reading time based on content
 * @param content - The HTML content of the blog post
 * @returns Reading time string (e.g., "4-5 min", "1 min")
 */
export const calculateReadingTime = (content: string): string => {
  if (!content || content.trim() === '') {
    return '1 min';
  }

  // Remove HTML tags and get plain text
  const plainText = content.replace(/<[^>]*>/g, '');
  
  // Count words (split by whitespace and filter empty strings)
  const wordCount = plainText
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0)
    .length;

  // Calculate reading time (200 words per minute)
  const readingTimeMinutes = wordCount / 200;

  // Format the reading time
  if (readingTimeMinutes < 1) {
    return '1 min';
  } else if (readingTimeMinutes < 2) {
    return '1-2 min';
  } else if (readingTimeMinutes < 3) {
    return '2-3 min';
  } else if (readingTimeMinutes < 4) {
    return '3-4 min';
  } else if (readingTimeMinutes < 5) {
    return '4-5 min';
  } else if (readingTimeMinutes < 10) {
    return `${Math.floor(readingTimeMinutes)}-${Math.ceil(readingTimeMinutes)} min`;
  } else {
    // For longer articles, just round to nearest minute
    const roundedMinutes = Math.round(readingTimeMinutes);
    return `ca. ${roundedMinutes} min`;
  }
};

/**
 * Gets word count from HTML content
 * @param content - The HTML content
 * @returns Number of words
 */
const getWordCount = (content: string): number => {
  if (!content || content.trim() === '') {
    return 0;
  }

  const plainText = content.replace(/<[^>]*>/g, '');
  return plainText
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0)
    .length;
};

/**
 * Estimates reading time in minutes (as number)
 * @param content - The HTML content
 * @returns Reading time in minutes
 */
const getReadingTimeMinutes = (content: string): number => {
  const wordCount = getWordCount(content);
  return Math.max(1, Math.round(wordCount / 200));
};